import { clone } from "ramda";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";

import { LoadingSpinner } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { type Tool } from "../../graphql.js";
import { type IIdentifiable } from "../../redux/modules/table-data.js";
import { checkInternet, loadAllTools } from "../../redux/modules/tools-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import NCBIAgentWizard from "../playbooks/ncbi/NCBIAgentWizard.js";
import { CELL_TYPE } from "@/settings/settings.js";

export interface IToolsAdminContext {
  hasInternetConnection: boolean;
}

interface IToolAdminPageAppStateProps {
  tools: Tool[];
  isLoadingSessions: boolean;
  hasInternetConnection: boolean;
  showConfigureDialog: boolean;
}

interface IToolAdminPageDispatchProps {
  loadTools: () => Promise<Tool[]>;
  checkInternet: (url: string) => Promise<boolean>;
}

export interface IToolAdminPageProps extends IToolAdminPageAppStateProps, IToolAdminPageDispatchProps {}

function mapStateToProps(state: IAppState): IToolAdminPageAppStateProps {
  return {
    tools: state.toolsAdmin.tools,
    isLoadingSessions: state.toolsAdmin.isLoadingTools || state.toolsAdmin.isCheckingInternet,
    hasInternetConnection: state.toolsAdmin.hasInternetConnection,
    showConfigureDialog: false,
  };
}

function mapDispatchToProps(dispatch): IToolAdminPageDispatchProps {
  return {
    loadTools: () => dispatch(loadAllTools()),
    checkInternet: () => dispatch(checkInternet(null)),
  };
}

interface IToolTableState {
  rowData: IIdentifiable[];
  showAddNewToolDialog: boolean;
  showConfigureDialog: boolean;
}

/**
 * Tool Admin Page main component
 */
class AdminToolsPage extends Component<IToolAdminPageProps, IToolTableState> {
  // private toolToEdit: ITool = null; // the selected tool, currently not used

  private static adminToolsColumnDefs: any[] = [
    {
      name: "Name",
      field: "tool.name",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Description",
      field: "tool.description",
      cellType: CELL_TYPE.STRING,
    },
  ];

  constructor(props: IToolAdminPageProps) {
    super(props);
    this.state = {
      rowData: null,
      showAddNewToolDialog: false,
      showConfigureDialog: false,
    };
  }

  loadTools = () => {
    this.props.checkInternet(null).then((_hasInternet: boolean) => {
      this.props.loadTools().then((tools: Tool[]) => {
        this.setState({ rowData: this.createRowData([], tools) });
      });
    });
  };

  componentDidMount() {
    this.loadTools();
  }

  toolPropContainsTerm = (tool: Tool, terms: string[]) => {
    if (terms === null || terms.length === 0) {
      return true;
    }
    const fieldValues = [tool.name, tool.description];
    for (let i = 0; i < terms.length; ++i) {
      let foundTerm = false;
      for (let j = 0; j < fieldValues.length; ++j) {
        if (fieldValues[j].toLowerCase().includes(terms[i])) {
          foundTerm = true;
          break;
        }
      }
      if (!foundTerm) {
        return false;
      }
    }
    return true;
  };

  createRowData = (terms: string[], tools: Tool[]): IIdentifiable[] => {
    return tools
      .filter((tool: Tool) => this.toolPropContainsTerm(tool, terms))
      .map((tool: Tool) => {
        return {
          id: tool.id,
          tool: clone(tool), // we have to clone the tool because of a bug in ag-grid 13 (AG-771)
        };
      });
  };

  searchExprChanged = (_expr: string, terms: string[]) => {
    return new Promise<void>((resolve) => {
      const newRowData = this.createRowData(terms, this.props.tools);
      this.setState({ rowData: newRowData });
      resolve();
    });
  };

  handleNameClick = (_tool: Tool) => {
    // this.toolToEdit = tool;
    this.setState({ showConfigureDialog: true });
  };

  private cellRendererParams = {
    "tool.name": {
      handleToolClicked: this.handleNameClick,
    },
  };

  render(): ReactElement {
    const adminContext: IToolsAdminContext = { hasInternetConnection: this.props.hasInternetConnection };

    return (
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <PageHeader
          title={"Admin Tools"}
          toolsWidgets={[
            <AsyncSearchWithExprParser onSearchExprChange={this.searchExprChanged} key={"admin-tools-page-widget-1"} />,
          ]}
        />
        {this.props.isLoadingSessions ? (
          <LoadingSpinner inCenter />
        ) : (
          <div style={{ height: "calc(100% - 50px)" }}>
            <InMemoryTableWithSelectionModel
              columns={AdminToolsPage.adminToolsColumnDefs}
              rowGridData={this.state.rowData}
              useSelectionModel={false}
              uniqueGridIdentifier={"admin-tools"}
              cellRendererParamsLookup={this.cellRendererParams}
              gridContext={{ adminContext: adminContext }}
            />
          </div>
        )}
        {this.state.showConfigureDialog && (
          <NCBIAgentWizard onClose={() => this.setState({ showConfigureDialog: false })} />
        )}
      </div>
    );
  }
}

export default connect<IToolAdminPageAppStateProps, IToolAdminPageDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(AdminToolsPage);
